import GameModel from "../../GameModel";
import { Settings } from "../../Settings";
import PartyTarget from "../character/PartyTarget";
import Crew from "../crew/Crew";
import Damage from "../crew/Damage";
import GridTile from "../grid/GridTile";
import VectorField from "../grid/VectorField";
import WorldGrid from "../grid/WorldGrid";
import BigNum from "../math/BigNum";
import VectorFieldGenerator from "../path/VectorFieldGenerator";
import MineMode from "./MineMode";
import { MineModes } from "./MineModes";

const VectorFieldDirectionCode = {
    LOCKED_NO_MOVE: -3,
    MOVE_UP_LARGE: -2,
    MOVE_UP_SMALL: -1,
    CURRENT: 0,
    MOVE_DOWN_SMALL: 1,
    MOVE_DOWN_LARGE: 2
}

export default class MineControl extends GameModel {
    /**
     * 
     * @param {Crew} crew 
     * @param {PartyTarget} partyTarget 
     * @param {Damage} damage
     * @param {WorldGrid} worldGrid 
     * @param {VectorFieldGenerator} vectorFieldGenerator 
     * @param {VectorField} vectorField 
     */
    constructor(crew, partyTarget, damage, worldGrid, vectorFieldGenerator, vectorField) {
        super();

        /**
         * @constant
         * @type {Crew}
         */
        this.crew = crew;
        /**
         * @constant
         * @type {PartyTarget}
         */
        this.partyTarget = partyTarget;
        /**
         * @constant
         * @type {Damage}
         */
        this.damage = damage;
        /**
         * @constant
         * @type {WorldGrid}
         */
        this.worldGrid = worldGrid;
        /**
         * @constant
         * @type {VectorFieldGenerator}
         */
        this.vectorFieldGenerator = vectorFieldGenerator;
        /**
         * @constant
         * @type {VectorField}
         */
        this.vectorField = vectorField;

        /**
         * @constant
         * @type {MineMode[]}
         */
        this.crewModes = [MineModes.MINE_DEEPER, MineModes.MINE_SHALLOWER, MineModes.MINE_CURRENT, MineModes.MINE_AUTO];

        /**
         * This is used for calculating target row for MINE_DEEPER and MINE_SHALLOWER, not path finding.
         * @constant
         * @type {number}
         */
        this.mineTileStep = 40;


        /**
         * @constant
         * @type {BigNum}
         */
        this.prevTotalPickDamagePerTurn = new BigNum(0);
        /**
         * @type {MineMode}
         */
        this.prevMode = null;

        /**
         * @constant
         * @type {BigNum}
         */
        this.workingHealth = new BigNum(0);

        /**
         * @constant
         * @type {BigNum}
         */
        this.tileHealthPerDamageThreshold = new BigNum(0.05); // this was 0.1

        ////////////////////////////////////////
        // Brain Debug
        ////////////////////////////////////////

        /**
         * @type {boolean}
         */
        this.debugTileHealthNegligible = false;
        /**
         * @type {boolean}
         */
        this.debugSearchGridSmall = false;
        /**
         * @type {boolean}
         */
        this.debugIgnorePremium = false;
        /**
         * @type {boolean}
         */
        this.debugIgnoreCommon = false;
        /**
         * @type {string}
         */
        this.debugGoalDescription = '';

        ///////////////////////////////////////////
        // Stateful properties
        ///////////////////////////////////////////

        /**
         * @type {MineMode}
         */
        this.activeMode = MineModes.MINE_AUTO;
    }

    /**
     * @param {MineMode} mineMode 
     */
    setModeActive(mineMode) {
        this.activeMode = mineMode;
    }

    /**
     * This is called by the save loader.
     * @param {string} modeId 
     */
    setActiveModeById(modeId) {

        const mode = this.findModeById(modeId);
        if (!mode) {
            console.log('MineControl.setActiveModeById() Failed to find mode id: ' + modeId);
            return;
        }

        this.setModeActive(mode);
    }

    /**
     * @private
     * @param {string} modeId 
     * @returns {MineMode}
     */
    findModeById(modeId) {
        for (let i = 0; i < this.crewModes.length; i++) {
            const mode = this.crewModes[i];
            if (mode.id === modeId) {
                return mode;
            }
        }
        return null;
    }

    updateForTurn() {
        // We need to update the vector field before we do character AI.

        const crewCenterTile = this.crew.calculateCrewCenterTile();
        if (!crewCenterTile) {
            console.log('MineControl.updateForTurn() no crew center tile');
            return;
        }

        // See if we need to move the target up or down based on where the party is relative to the current target.

        /////////////////////////////////////////////
        // Calculate Target Row
        /////////////////////////////////////////////

        if (this.activeMode === MineModes.MINE_AUTO) {
            const mineSecondsDifference = this.partyTarget.targetMineSeconds !== this.partyTarget.defaultMineSeconds;
            const pickDamageDifference = !this.prevTotalPickDamagePerTurn.equals(this.damage.totalPickDamagePerTurn);

            if (mineSecondsDifference || pickDamageDifference) {
                this.partyTarget.targetMineSeconds = this.partyTarget.defaultMineSeconds;
                this.prevTotalPickDamagePerTurn.copy(this.damage.totalPickDamagePerTurn);

                this.partyTarget.calculateTargetRow();
            }
        }
        else {
            this.checkForTargetAdjust(crewCenterTile, this.prevMode !== this.activeMode);
        }
        this.prevMode = this.activeMode;

        /////////////////////////////////////////////
        // Tile Health Negligible
        /////////////////////////////////////////////

        this.vectorField.tileHealthNegligible = this.isTileHealthNegligible(crewCenterTile);
        this.debugTileHealthNegligible = this.vectorField.tileHealthNegligible;

        /////////////////////////////////////////////
        // Mine Target Grid
        /////////////////////////////////////////////

        this.applyMiningLogic(crewCenterTile);
    }

    /**
     * @private
     * @param {GridTile} tile 
     * @returns {boolean}
     */
    isTileHealthNegligible(tile) {
    
        // Calc the health for the current row. don't just grab the health from the center tile
        // because it may have been mined and be 0.
        tile.tileType.layerDescription.calculateTileHealth(tile.getTileRow(), this.workingHealth);

        // Compare the health to the crew total damage.
        this.workingHealth.div(this.damage.totalPickDamagePerTurn);

        // If damage is MUCH greater than health, then health/damage will become very small.

        return this.workingHealth.lessThan(this.tileHealthPerDamageThreshold);
    }

    /**
     * @private
     * @param {GridTile} centerTile
     * @param {boolean} modeChanged
     */
    checkForTargetAdjust(centerTile, modeChanged) {

        const tileRow = centerTile.getTileRow();

        // If the mine mode had changed since the last turn, then we need to do
        // the target row recalculation regardless of the current min/max target rows.
        if (modeChanged) {
            if (this.activeMode === MineModes.MINE_CURRENT) {
                this.applyMineCurrentTargetAdjust(tileRow);
            }
            else if (this.activeMode === MineModes.MINE_SHALLOWER) {
                this.applyMineShallowerTargetAdjust(tileRow);
            }
            else if (this.activeMode === MineModes.MINE_DEEPER) {
                this.applyMineDeeperTargetAdjust(tileRow);
            }
            return;
        }

        const targetRow = this.partyTarget.targetRow;
        const minRow = targetRow - 15;  // TODO CONSOLIDATE THESE ROW NUMBERS!
        const maxRow = targetRow + 15;

        if (tileRow < minRow) {
            // We are shallower than our target, so mining logic will currently cause us to mine downwards.

            if (this.activeMode === MineModes.MINE_SHALLOWER) {
                this.applyMineShallowerTargetAdjust(tileRow);
            }
        }
        else if (tileRow > maxRow) {
            // We are deeper than our target. Mining logic will currently cause us to mine upwards.

            if (this.activeMode === MineModes.MINE_DEEPER) {
                this.applyMineDeeperTargetAdjust(tileRow);
            }
        }
        else {
            // We are within our target range. 

            if (this.activeMode === MineModes.MINE_DEEPER) {
                this.applyMineDeeperTargetAdjust(tileRow);
            }
            else if (this.activeMode === MineModes.MINE_SHALLOWER) {
                this.applyMineShallowerTargetAdjust(tileRow);
            }
        }
    }

    /**
     * @private
     * @param {number} tileRow
     */
    applyMineCurrentTargetAdjust(tileRow) {
        this.partyTarget.deriveTargetSecondsForRow(tileRow);
        this.partyTarget.calculateTargetRow();
    }

    /**
     * @private
     * @param {number} tileRow
     */
    applyMineShallowerTargetAdjust(tileRow) {
        
        // Make sure we don't keep moving up into the air
        const shallowerRow = Math.max(Settings.target.minTargetRow, tileRow - this.mineTileStep);

        this.partyTarget.deriveTargetSecondsForRow(shallowerRow);
        this.partyTarget.calculateTargetRow();
    }

    /**
     * @private
     * @param {number} tileRow
     */
    applyMineDeeperTargetAdjust(tileRow) {
        this.partyTarget.deriveTargetSecondsForRow(tileRow + this.mineTileStep);
        this.partyTarget.calculateTargetRow();
    }

    /**
     * @private
     * @param {GridTile} crewCenterTile
     */
    applyMiningLogic(crewCenterTile) {
        // Mine up or down based on party target min/max

        const tile = this.vectorField.centerTile ? this.vectorField.centerTile : crewCenterTile;

        const vectorFieldTileRow = tile.getTileRow();
        const crewTileRow = crewCenterTile.getTileRow();    
        const targetRow = this.partyTarget.targetRow;
        const priorityTileVisible = this.vectorField.isPriorityTileVisible();

        let directionCode = 0;

        if (this.activeMode === MineModes.MINE_CURRENT) {
            if (crewTileRow < -5) {
                directionCode = VectorFieldDirectionCode.MOVE_DOWN_LARGE;
            }
            else {
                directionCode = VectorFieldDirectionCode.CURRENT;
            }
        }
        else if (this.activeMode === MineModes.MINE_SHALLOWER) {            
            // Don't let the crew mine up into the sky.
            directionCode = vectorFieldTileRow > Settings.target.minTargetRow ? VectorFieldDirectionCode.MOVE_UP_LARGE : VectorFieldDirectionCode.CURRENT;
        }
        else if (this.activeMode === MineModes.MINE_DEEPER) {
            directionCode = VectorFieldDirectionCode.MOVE_DOWN_LARGE;
        }
        else { // AUTO        

            const minRowMinor = Math.max(Settings.target.minTargetRow, targetRow - 15);
            const minRowMajor = Math.max(Settings.target.minTargetRow, targetRow - 25);
            const maxRowMinor = targetRow + 15;
            const maxRowMajor = targetRow + 25;
    
            if (vectorFieldTileRow < minRowMajor) {
                directionCode = VectorFieldDirectionCode.MOVE_DOWN_LARGE;
            }
            else if (vectorFieldTileRow < minRowMinor) {
                directionCode = VectorFieldDirectionCode.MOVE_DOWN_SMALL;
            }
            else if (vectorFieldTileRow > maxRowMajor) {
                directionCode = VectorFieldDirectionCode.MOVE_UP_LARGE;
            }
            else if (vectorFieldTileRow > maxRowMinor) {
                directionCode = VectorFieldDirectionCode.MOVE_UP_SMALL;
            }
            else {
                directionCode = VectorFieldDirectionCode.CURRENT;
            }
        }

        // We only worry about priority tiles bieng visbile if we are in CURRENT/MINE_X_SMALL mode
        if (priorityTileVisible) {
            // We only go into locked mode if we are not focused on moving up/down
            if (directionCode !== VectorFieldDirectionCode.MOVE_DOWN_LARGE && directionCode !== VectorFieldDirectionCode.MOVE_UP_LARGE) {
                directionCode = VectorFieldDirectionCode.LOCKED_NO_MOVE;
            }
        }

        let moveUpOk = true;
        let moveDownOk = true;
        let bigStepY = false;
        let bigStepX = false;
        let ignorePremiumTiles = false;
        let ignoreCommonTiles = this.vectorField.tileHealthNegligible;
        let smallGridMode = false;

        if (directionCode === VectorFieldDirectionCode.LOCKED_NO_MOVE) {
            this.debugGoalDescription = 'mine priority tiles';
        }
        else if (directionCode === VectorFieldDirectionCode.MOVE_DOWN_LARGE) {
            // MOVE DOWN A LOT
            moveUpOk = false;
            moveDownOk = vectorFieldTileRow < crewTileRow + 6; // Don't move down if the VF is already good bit deeper than the crew
            bigStepY = true;            
            ignorePremiumTiles = this.vectorField.tileHealthNegligible;
            ignoreCommonTiles = true; // always ignore common tiles when up/down is large.
            smallGridMode = true;
            this.debugGoalDescription = 'mine downwards';
        }
        else if (directionCode === VectorFieldDirectionCode.MOVE_DOWN_SMALL) {
            // MOVE DOWN A LITTLE
            moveUpOk = false;
            moveDownOk = vectorFieldTileRow < crewTileRow + 10; // Don't move down if the VF is already good bit deeper than the crew
            this.debugGoalDescription = 'mine down a little';
        }
        else if (directionCode === VectorFieldDirectionCode.MOVE_UP_LARGE) {
            // MOVE UP A LOT
            moveDownOk = false;
            bigStepY = true;
            moveUpOk = vectorFieldTileRow > crewTileRow - 6;
            ignorePremiumTiles = this.vectorField.tileHealthNegligible;
            ignoreCommonTiles = true; // always ignore common tiles when up/down is large.
            smallGridMode = true;
            this.debugGoalDescription = 'mine upwards';
        }
        else if (directionCode === VectorFieldDirectionCode.MOVE_UP_SMALL) {
            // MOVE UP A LITTLE
            moveDownOk = false;
            moveUpOk = vectorFieldTileRow > crewTileRow - 10;
            this.debugGoalDescription = 'mine up a little';
        }
        else {
            // CURRENT
            // only let the VF center move towards the target row. and then stay there.
            moveDownOk = vectorFieldTileRow < targetRow;
            moveUpOk = vectorFieldTileRow > targetRow;
            bigStepX = this.vectorField.tileHealthNegligible;
            this.debugGoalDescription = 'mine around target depth';
        }

        // See if the fall speed bonus should be active
        this.crew.values.fallSpeedBonusActive.setValue(ignorePremiumTiles && directionCode === VectorFieldDirectionCode.MOVE_DOWN_LARGE);

        const vectorFieldCenterTile = this.calculateVectorFieldCenterTile(crewCenterTile, this.vectorField.centerTile, directionCode, moveUpOk, moveDownOk, bigStepY, bigStepX);

        /////////////////////////////////////////////////
        this.debugSearchGridSmall = smallGridMode;
        this.debugIgnorePremium = ignorePremiumTiles;
        this.debugIgnoreCommon = ignoreCommonTiles;
        //////////////////////////////////////////

        //// destinationDirection 0=none, 1=down, 2=up, 3=right
        //////const destinationDirection = directionCode < 0 ? 2 : directionCode > 0 ? 1 : 3;

        // const destinationDirection = directionCode === VectorFieldDirectionCode.LOCKED_NO_MOVE ? 0
        //     : directionCode < 0 ? 2 : directionCode > 0 ? 1 : 3;

        let destinationDirection = 0;
        switch (directionCode) {
            case VectorFieldDirectionCode.CURRENT:
                destinationDirection = 3; // right
                break;
            case VectorFieldDirectionCode.LOCKED_NO_MOVE:
                destinationDirection = 0; // no direction tiles
                break;
            case VectorFieldDirectionCode.MOVE_DOWN_LARGE:
            case VectorFieldDirectionCode.MOVE_DOWN_SMALL:
                destinationDirection = 1; // move down
                break;
            case VectorFieldDirectionCode.MOVE_UP_LARGE:
            case VectorFieldDirectionCode.MOVE_UP_SMALL:
                destinationDirection = 2; // move up
                break;
        }

        // const VectorFieldDirectionCode = {
        //     LOCKED_NO_MOVE: -3,
        //     MOVE_UP_LARGE: -2,
        //     MOVE_UP_SMALL: -1,
        //     CURRENT: 0,
        //     MOVE_DOWN_SMALL: 1,
        //     MOVE_DOWN_LARGE: 2
        // }
        

        this.vectorFieldGenerator.generateVectorField(vectorFieldCenterTile, ignoreCommonTiles, ignorePremiumTiles, smallGridMode, destinationDirection); 
    }


    /**
     * @private
     * @param {GridTile} crewCenterTile 
     * @param {GridTile} vectorFieldCenterTile 
     * @param {number} directionCode 
     * @param {boolean} moveUpOk
     * @param {boolean} moveDownOk
     * @param {boolean} bigStepY
     * @param {boolean} bigStepX
     * @returns {GridTile}
     */
    calculateVectorFieldCenterTile(crewCenterTile, vectorFieldCenterTile, directionCode, moveUpOk, moveDownOk, bigStepY, bigStepX) {
        if (!vectorFieldCenterTile) {
            console.log('MineControl.calculateVectorFieldCenterTile() - no vector field center tile');
            return crewCenterTile;
        }

        // code:
        // -3 locked
        // -2 shallow major
        // -1 shallow minor
        //  0 current
        //  1 deeper minor
        //  2 deeper major

        // If we are locked, then don't move the VF center.
        if (directionCode === VectorFieldDirectionCode.LOCKED_NO_MOVE) {
            return vectorFieldCenterTile;
        }

        // X Step : Simpler than y step.

        // Only ever take large steps to the right - because that's the direction of the destination tiles.
        const xStep = bigStepX ? (4 * Settings.tile.size) : Settings.tile.size; 
        const dx = crewCenterTile.origin.x - vectorFieldCenterTile.origin.x;
        const x = dx == 0 ? crewCenterTile.origin.x : dx < 0 ? vectorFieldCenterTile.origin.x - Settings.tile.size : vectorFieldCenterTile.origin.x + xStep;

        let y = vectorFieldCenterTile.origin.y;
        if (directionCode === VectorFieldDirectionCode.MOVE_UP_LARGE || directionCode === VectorFieldDirectionCode.MOVE_UP_SMALL) {
            // There is a theshold where we do not want to keep moving up even if we are in move up mode.
            if (moveUpOk) {
                const yStep = bigStepY ? (4 * Settings.tile.size) : Settings.tile.size;
                y = vectorFieldCenterTile.origin.y - yStep;
            }
        }
        else if (directionCode === VectorFieldDirectionCode.MOVE_DOWN_LARGE || directionCode === VectorFieldDirectionCode.MOVE_DOWN_SMALL) {
            // There is a theshold where we do not want to keep moving down even if we are in move down mode.
            if (moveDownOk) {
                const dy = crewCenterTile.origin.y - vectorFieldCenterTile.origin.y;

                const yStep = bigStepY ? this.calculateBigYStep(dy) : Settings.tile.size; // *********************

                y = vectorFieldCenterTile.origin.y + yStep;
            }
        }
        else {
            const dy = crewCenterTile.origin.y - vectorFieldCenterTile.origin.y;

            const yStep = bigStepY ? this.calculateBigYStep(dy) : Settings.tile.size; // *******************

            if (dy < 0 && moveUpOk) {
                y = vectorFieldCenterTile.origin.y - yStep;
            }
            else if (dy > 0 && moveDownOk) {
                y = vectorFieldCenterTile.origin.y + yStep;
            }
        }

        const newCenter = this.worldGrid.findGridTileXY(x, y);
        if (!newCenter) {
            return vectorFieldCenterTile;
        }
        return newCenter;
    }

    // TODO FAST FALLING - WE ARE CATCHING UP TO THE EDGE OF THE GRID WITHIN THE TURN.

    /**
     * How far should the vector field shift in the y-direction.
     * @private
     * @param {number} dy crew y-coordinate minus vector field y-coodinate. 
     * @returns {number} 
     */
    calculateBigYStep(dy) {
        if (dy <= 0) {
            return 4 * Settings.tile.size;
        }

        if (dy > 4 * Settings.tile.size) {
            return dy;
        }
        else {
            return 4 * Settings.tile.size;
        }
    }

    /////////////////////////////////////////////////
    // GameModel
    /////////////////////////////////////////////////

    /**
     * @override
     */
    resetFull() {
        this.setModeActive(MineModes.MINE_AUTO);       
    }

    /**
     * @override
     */
    resetForPrestige() {
        // Don't reset the selected mode during a prestige.
    }

}